/**
 * Sentry Error Monitoring — Lightweight integration
 *
 * Captures unhandled errors and sends them to Sentry.
 * Uses the Sentry Browser SDK lazy-loaded to avoid bundle impact.
 *
 * Setup:
 * 1. Create a Sentry project at sentry.io
 * 2. Set VITE_SENTRY_DSN in .env
 * 3. Import and call initSentry() in main.tsx
 *
 * If VITE_SENTRY_DSN is not set, all functions are no-ops.
 */

let sentryInstance: any = null;
let initialized = false;

const DSN = import.meta.env?.VITE_SENTRY_DSN || '';

export async function initSentry() {
  if (initialized || !DSN) return;
  initialized = true;

  try {
    // Dynamic import with variable to prevent Vite from resolving at build time
    const pkg = '@sentry/react';
    const Sentry = await import(/* @vite-ignore */ pkg);
    Sentry.init({
      dsn: DSN,
      environment: import.meta.env?.MODE || 'production',
      release: `intentdrive@${import.meta.env?.VITE_APP_VERSION || '1.0.0'}`,
      tracesSampleRate: 0.1,
      beforeSend(event: any) {
        if (event.request?.cookies) delete event.request.cookies;
        if (event.request?.headers?.authorization) event.request.headers.authorization = '[REDACTED]';
        return event;
      },
    });
    sentryInstance = Sentry;
    console.log('[Sentry] Initialized');
  } catch (err) {
    // Sentry package not installed — that's fine, all functions are no-ops
    console.log('[Sentry] Not available (install @sentry/react to enable)');
  }
}

export function captureException(error: unknown, context?: Record<string, unknown>) {
  if (!sentryInstance) {
    console.error('[Error]', error, context);
    return;
  }
  sentryInstance.captureException(error, { extra: context });
}

export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  if (!sentryInstance) {
    console.log(`[${level}]`, message);
    return;
  }
  sentryInstance.captureMessage(message, level);
}

export function setUser(id: string, email?: string) {
  if (!sentryInstance) return;
  sentryInstance.setUser({ id, email });
}

export function clearUser() {
  if (!sentryInstance) return;
  sentryInstance.setUser(null);
}
